import { FormControl, FormHelperText, FormLabel, OutlinedInput } from "@mui/material";
import "bootstrap/dist/css/bootstrap.min.css";
import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logoIcon from "../../assets/images/logoIcon.svg";
import Alert from "../../components/dialog/Alert";
import Toaster from "../../components/toaster/toaster";
import Schema from "../../schema";
import { maskPhoneNumber, obfuscateEmail } from "../../utils/common";
import { otpVerify, resendLoginOtp } from "../users/action";

const RESEND_COOLDOWN_SECONDS = 30;

const TwoFactorAuth = () => {
  const initialValues = {
    otp: null,
  };
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const cooldownTimerRef = useRef(null);

  useEffect(() => {
    cooldownTimerRef.current = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(cooldownTimerRef.current);
  }, []);

  const handleResendOtp = () => {
    if (resendCooldown > 0) return;
    dispatch(resendLoginOtp(() => {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = setInterval(() => {
        setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
    }));
  };

  // OTP is now mandatory for every login, so default the destination to email
  // when no explicit two-factor channel is configured on the account.
  const tfaType = state?.userData?.twoFactorAuthType?.toLowerCase() || "email";
  let email = state?.userData?.email
    ? obfuscateEmail(state?.userData?.email)
    : "";
  let phone = state?.userData?.phoneNumber
    ? maskPhoneNumber(state?.userData?.phoneNumber)
    : "";
  let otpDestination =
    tfaType === "both"
      ? `${email}${phone ? " and " + phone : ""}`
      : tfaType === "sms"
        ? phone || email
        : email;

  const clientLogos = [
    "https://www.inflyx.co/images/clients/Avaan.png",
    "https://www.inflyx.co/images/clients/Plush.png",
    "https://www.inflyx.co/images/clients/Pubjab_Govt.png",
    "https://www.inflyx.co/images/clients/Vim.png",
    "https://www.inflyx.co/images/clients/acko.png",
    "https://www.inflyx.co/images/clients/ageas.png",
    "https://www.inflyx.co/images/clients/bajaj-finserv.png",
    "https://www.inflyx.co/images/clients/barbeque.svg",
    "https://www.inflyx.co/images/clients/basic.svg",
    "https://www.inflyx.co/images/clients/blur.png",
    "https://www.inflyx.co/images/clients/cars24.png",
    "https://www.inflyx.co/images/clients/country-delight.svg",
    "https://www.inflyx.co/images/clients/dotkey.svg",
    "https://www.inflyx.co/images/clients/gcc.svg",
    "https://www.inflyx.co/images/clients/ghar_soap.png",
    "https://www.inflyx.co/images/clients/indrive.svg",
    "https://www.inflyx.co/images/clients/mars.png",
    "https://www.inflyx.co/images/clients/max_protein.svg",
    "https://www.inflyx.co/images/clients/nutrovally.svg",
    "https://www.inflyx.co/images/clients/plix.png",
    "https://www.inflyx.co/images/clients/quillbot.png",
    "https://www.inflyx.co/images/clients/raw.svg",
    "https://www.inflyx.co/images/clients/rosa_herbalcare.png",
    "https://www.inflyx.co/images/clients/shark.svg",
    "https://www.inflyx.co/images/clients/smart_bazar.png",
    "https://www.inflyx.co/images/clients/soulflower.svg",
    "https://www.inflyx.co/images/clients/sujata.png",
    "https://www.inflyx.co/images/clients/surf_excel.png",
    "https://www.inflyx.co/images/clients/suroskin.svg",
    "https://www.inflyx.co/images/clients/tinder.png",
    "https://www.inflyx.co/images/clients/virgo.png",
  ];

  return (
    <>
      <Toaster />
      <div className="page-container">
        <div className="login-wrapper">
          {/* LEFT SIDE */}

          <div className="login-left">
            <img src={logoIcon} className="logo" alt="Inflyx" />

            <p className="small-title">Almost there</p>

            <h2 className="login-title">
              Verify your <span>Account</span>
            </h2>

            <Formik
              initialValues={initialValues}
              validationSchema={Schema.otpVerfication}
              enableReinitialize={true}
              onSubmit={(values) => {
                dispatch(otpVerify(values, navigate));
              }}
            >
              {({ errors, handleChange, touched, values }) => (
                <Form>
                  <p className="otp-hint">
                    We have sent you a verification code on {otpDestination}
                  </p>
                  <FormControl fullWidth sx={{ marginBottom: 2 }}>
                    <FormLabel htmlFor="otp" required focused={false}>
                      Verification Code
                    </FormLabel>
                    <OutlinedInput
                      id="otp"
                      type="text"
                      name="otp"
                      placeholder="Enter 6 digit OTP"
                      value={values.otp ?? ""}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (/^\d{0,6}$/.test(newValue)) {
                          handleChange(e);
                        }
                      }}
                      inputMode="numeric"
                      inputProps={{
                        maxLength: 6,
                        style: { MozAppearance: "textfield" },
                      }}
                      error={Boolean(touched.otp) && Boolean(errors.otp)}
                    />
                    {touched.otp && (
                      <FormHelperText variant="standard" error>
                        {errors.otp}
                      </FormHelperText>
                    )}
                  </FormControl>
                  <p className="resend-otp">
                    Didn't receive the code?{" "}
                    <button
                      type="button"
                      className="resend-otp-btn"
                      onClick={handleResendOtp}
                      disabled={resendCooldown > 0}
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
                    </button>
                  </p>
                  <button className="w-100 mt-2 login-btn" type="submit">
                    Continue
                  </button>
                  <p className="text-center redirectLink mt-4 fs14">
                    <Link
                      to="/login"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/login");
                      }}
                      className="text-decoration-none"
                    >
                      Back to Login
                    </Link>
                  </p>
                </Form>
              )}
            </Formik>
          </div>

          {/* RIGHT SIDE */}

          <div className="login-right">
            <div className="badge">🚀 Creator Discovery Platform</div>

            <h1 className="heading">
              Find the Perfect <span>Influencers</span>
              <br />
              For Every Campaign
            </h1>

            <p className="description">
              Discover creators, analyze historical performance, evaluate
              audience quality, and measure campaign ROI with AI-powered
              insights built for modern brands.
            </p>

            <div className="features">
              <div className="feature">
                <div className="feature-icon">🧠</div>
                <div className="feature-text">
                  <h4>AI Matchmaking</h4>
                  <p>Find creators aligned with your campaign goals.</p>
                </div>
              </div>

              <div className="feature">
                <div className="feature-icon">📈</div>
                <div className="feature-text">
                  <h4>Historical Insights</h4>
                  <p>Analyze previous campaign performance.</p>
                </div>
              </div>

              <div className="feature">
                <div className="feature-icon">🎯</div>
                <div className="feature-text">
                  <h4>Campaign Analytics</h4>
                  <p>Track engagement, reach and ROI metrics.</p>
                </div>
              </div>
            </div>

            <div className="clients-section">
              <div className="clients-title">Trusted By Leading Brands</div>

              <div className="logo-slider">
                <div className="logo-track">
                  {[...clientLogos, ...clientLogos].map((logo, index) => (
                    <img key={index} src={logo} alt={`Client ${index}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Alert />
      <style>{`

.login-wrapper{
    width:auto;
    max-width:100%;
    background:rgba(255,255,255,0.05);
    backdrop-filter:blur(18px);
    -webkit-backdrop-filter:blur(18px);


    overflow:hidden;
    display:grid;
    grid-template-columns:0.85fr 1fr;
    position:relative;
    z-index:2;
}

/* =========================
   LEFT SIDE
========================= */

.login-left{
    background:#fff;
    padding:60px;
}

.logo{
    width:90px;
    margin-bottom:20px;
}

.small-title{
    color:#7c7c7c;
    font-size:15px;
    margin-bottom:10px;
}

.login-title{
    font-size:38px;
    font-weight:700;
    margin-bottom:40px;
}

.login-title span{
    background:linear-gradient(90deg,#6b6cff,#e445a3);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
}

.otp-hint{
    color:#7c7c7c;
    font-size:14px;
    margin-bottom:22px;
}

.resend-otp{
    text-align:right;
    font-size:14px;
    color:#7c7c7c;
    margin:-10px 0 18px;
}

.resend-otp-btn{
    background:none;
    border:none;
    padding:0;
    font-size:14px;
    font-weight:600;
    color:#6b6cff;
    cursor:pointer;
}

.resend-otp-btn:disabled{
    color:#aaa;
    cursor:not-allowed;
}

.form-group{
    margin-bottom:22px;
}

.form-group label{
    display:block;
    margin-bottom:8px;
    font-weight:500;
}

.form-control{
    width:100%;
    height:56px;
    border:none;
    border-radius:12px;
    background:#f5f7fb;
    padding:0 18px;
    font-size:15px;
}

.form-control:focus{
    outline:none;
    border:2px solid #6b6cff;
}

.row{
    display:flex;
    justify-content:space-between;
    align-items:center;
    gap:10px;
    margin:20px 0;
}

.login-btn{
    width:100%;
    height:50px;
    border:none;
    border-radius:12px;
    cursor:pointer;
    color:#fff;
    font-size:18px;
    font-weight:600;
    background:linear-gradient(90deg,#5b6dff,#e83c9d);
}

.signup{
    text-align:center;
    margin-top:25px;
}

/* =========================
   RIGHT SIDE
========================= */

.login-right{
    padding:70px;
    color:#fff;
    display:flex;
    flex-direction:column;
    justify-content:center;
    overflow:hidden;
    background: linear-gradient(90deg, #5b6dff, #e83c9d);
}

.badge{
    width:max-content;
    padding:10px 18px;
    border-radius:50px;
    background:rgba(255,255,255,.1);
    margin-bottom:30px;
}

.heading{
    font-size:48px;
    font-weight:700;
    line-height:1.2;
    margin-bottom:20px;
    opacity:0;
    transform:translateY(40px);
    animation:heroReveal 1.2s ease forwards;
}

.heading span{
    color:#ff69d6;
    position:relative;
}

.heading span::after{
    content:'';
    position:absolute;
    left:0;
    bottom:-4px;
    width:100%;
    height:4px;
    background:#ff69d6;
    transform:scaleX(0);
    transform-origin:left;
    animation:lineReveal 1s ease forwards 1s;
}

@keyframes heroReveal{
    to{
        opacity:1;
        transform:translateY(0);
    }
}

@keyframes lineReveal{
    to{
        transform:scaleX(1);
    }
}
.description{
    font-size:17px;
    color:#d6d6d6;
    line-height:1.8;
    margin-bottom:40px;
}

.features{
    display:flex;
    flex-direction:column;
    gap:22px;
}

.feature{
    display:flex;
    align-items:center;
    gap:15px;
}

.feature-icon{
    width:52px;
    height:52px;
    border-radius:12px;
    background:rgba(255,255,255,.12);
    display:flex;
    align-items:center;
    justify-content:center;
    font-size:24px;
}

.feature-text h4{
    font-size:20px;
    margin-bottom:4px;
}

.feature-text p{
    color:#d8d8d8;
    font-size:14px;
}

.stats{
    margin-top:50px;
    display:flex;
    gap:40px;
}

.stat h2{
    font-size:34px;
}

.stat p{
    color:#cfcfcf;
    font-size:14px;
}

/* =========================
   CLIENT LOGO SLIDER
========================= */

.clients-section{
    margin:35px 0;
}

.clients-title{
    font-size:13px;
    color:rgba(255,255,255,.7);
    text-transform:uppercase;
    letter-spacing:2px;
    margin-bottom:15px;
}

.logo-slider{
    width:100%;
    overflow:hidden;
    position:relative;
}

.logo-track{
    display:flex;
    align-items:center;
    gap:50px;
    width:max-content;
    animation:scroll 120s linear infinite;
    will-change:transform;
}

.logo-track img{
    height:40px;
    width:auto;
    max-width:140px;
    object-fit:contain;
    opacity:.9;
    transition:.3s ease;
}

.logo-track img:hover{
    opacity:1;
    transform:scale(1.25);
}

.logo-slider::before,
.logo-slider::after{
    content:'';
    position:absolute;
    top:0;

    z-index:2;
}

.logo-slider::before{
    left:0;
    background:linear-gradient(to right,#070314,transparent);
}

.logo-slider::after{
    right:0;
    background:linear-gradient(to left,#070314,transparent);
}

@keyframes scroll{
    from{
        transform:translateX(0);
    }
    to{
        transform:translateX(-50%);
    }
}

/* =========================
   TABLET
========================= */

@media (max-width:991px){

    .login-wrapper{
        grid-template-columns:1fr;
        max-width:700px;
    }

    .login-right{
        order:-1;
        padding:40px;
    }

    .login-left{
        padding:40px;
    }

    .heading{
        font-size:34px;
    }

    .description{
        font-size:15px;
    }

    .stats{
        flex-wrap:wrap;
        gap:25px;
    }

    .logo-track img{
        height:34px;
        max-width:120px;
    }
}

/* =========================
   MOBILE
========================= */

@media (max-width:768px){

    body{
        padding:15px;
    }

    body::before{
        width:350px;
        height:350px;
    }

    body::after{
        width:300px;
        height:300px;
    }

    .login-left,
    .login-right{
        padding:30px;
    }

    .login-title{
        font-size:30px;
    }

    .heading{
        font-size:28px;
        line-height:1.3;
    }

    .description{
        font-size:14px;
    }

    .feature-icon{
        width:46px;
        height:46px;
        font-size:20px;
        flex-shrink:0;
    }

    .feature-text h4{
        font-size:17px;
    }

    .feature-text p{
        font-size:13px;
    }

    .logo-track{
        gap:35px;
        animation:scroll 30s linear infinite;
    }

    .logo-track img{
        height:30px;
        max-width:100px;
    }
}

@media (max-width:576px){

    body{
        padding:10px;
    }

    .login-wrapper{
        border-radius:18px;
    }

    .login-left,
    .login-right{
        padding:24px;
    }

    .badge{
        font-size:12px;
        padding:8px 14px;
    }

    .login-title{
        font-size:26px;
        margin-bottom:25px;
    }

    .heading{
        font-size:24px;
    }

    .form-control{
        height:52px;
        font-size:14px;
    }

    .login-btn{
        height:52px;
        font-size:16px;
    }

    .row{
        flex-direction:column;
        align-items:flex-start;
    }

    .stats{
        flex-direction:column;
        gap:18px;
    }

    .stat h2{
        font-size:28px;
    }

    .logo-track{
        gap:25px;
        animation:scroll 25s linear infinite;
    }

    .logo-track img{
        height:26px;
        max-width:90px;
    }

    .clients-title{
        font-size:11px;
        letter-spacing:1px;
    }
}

@media (max-width:420px){

    .logo{
        width:70px;
    }

    .login-title{
        font-size:24px;
    }

    .heading{
        font-size:22px;
    }

    .feature-icon{
        width:42px;
        height:42px;
        font-size:18px;
    }

    .logo-track img{
        height:24px;
        max-width:80px;
    }
}


      `}</style>
    </>
  );
};

export default TwoFactorAuth;
